import {Trans} from '@lingui/macro';
import {compact, last, noop} from 'lodash';
import {DateTime} from 'luxon';
import React from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {isElectron} from '../../../libs/shared/helpers/src/environment/platformHelpers';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {buildTeamCounterKey, findBadgeCounter} from '../../helpers/counterHelpers';
import {
  buildAddInboxSettingsPath,
  buildAddTeammateSettingsPath,
  buildTeamPath,
} from '../../helpers/modelHelpers';
import {AppParametricSelector, makeMapStateToProps} from '../../helpers/reselectHelpers';
import {MessageTypesEnum} from '../../models/api/messageModel';
import {
  CountersVisibilityModesEnum,
  OnboardingTipTypesEnum,
  StartGuideTaskTypesEnum,
} from '../../models/api/preferencesModel';
import {SearchHelpCenterResultModel} from '../../models/api/searchHelpCenterModel';
import {InboxesFiltersEnum, TeamsFoldersEnum} from '../../models/url/inboxesUrlModel';
import {
  InterfaceEventsEnum,
  InviteTeammatePopoverAccessPointsEnum,
  trackInterfaceEvent,
} from '../../services/tracking';
import {makeGetComposeChannelExcludingDemo} from '../../store/data/channels/channelsSelectors';
import {getCompany, getIsCustomer} from '../../store/data/company/companySelectors';
import {getCounters} from '../../store/data/dataSelectors';
import {updateHelpCenterSearchCacheAction} from '../../store/data/localPreferences/localPreferencesActions';
import {getHelpCenterSearchCache} from '../../store/data/localPreferences/localPreferencesSelectors';
import {composeMessageAction} from '../../store/data/messages/draftMessagesActions';
import {makeGetCountersVisibility} from '../../store/data/preferences/countersSelectors';
import {updateStartGuideTasksAction} from '../../store/data/preferences/preferencesActions';
import {getCompletedStartGuideTasks} from '../../store/data/preferences/preferencesSelectors';
import {
  getIsUserFirstGlobalAdmin,
  getIsUserGlobalAdmin,
  getUser,
  getUserNamespace,
} from '../../store/data/teammates/teammatesSelectors';
import {getFirstTeamUserIsAdminOf, getLastUpdatedTeam} from '../../store/data/teams/teamsSelectors';
import {getUserMetadata} from '../../store/data/userMetadata/userMetadataSelectors';
import {
  getHasActionCtasBundle,
  getHasNewTeammateInvite,
} from '../../store/featureFlags/featureFlagsSelectors';
import {openHelpChatAction} from '../../store/panels/app/appPanelActions';
import {makeGetAppPath} from '../../store/panels/app/appPanelSelectors';
import {makeGetAppPanelIdFromPanel} from '../../store/panels/panelsSelectors';
import {ViewerPanelId} from '../../store/panels/panelStateBase';
import {
  blogUrl,
  navigateToAddTeammateAction,
  navigateToLinkAction,
} from '../../store/stateless/navigationActions';
import {
  getHasExploredDemoInbox,
  getHasGlobalAdminStartGuide,
  getShouldAddIndividualInbox,
  getShouldAddTeamInbox,
  getShouldAddTeammate,
  getShouldExploreSettings,
} from '../../store/stateless/startGuideSelectors';
import {StartGuide} from './startGuide';
import {composeTeamInboxAccessRequest} from './startGuideActions';
import {StartGuideTaskDefinition, StartGuideTaskInteractionsEnum} from './startGuideCommon';

/*
 * Props.
 */

interface StartGuideContainerProps {
  panelId: ViewerPanelId;
}

/*
 * Redux.
 */

function makeGetTeamNamespace() {
  return createSelector(getLastUpdatedTeam, (team) => team?.namespace || '');
}

function makeGetHasConversationsInTeamInbox(
  getCountersVisibility: AppParametricSelector<any, CountersVisibilityModesEnum>,
) {
  return createSelector(
    [getCountersVisibility, getCounters, getLastUpdatedTeam],
    (countersVisibility, counters, lastUpdatedTeam) => {
      if (!lastUpdatedTeam) {
        return false;
      }

      const key = buildTeamCounterKey(lastUpdatedTeam.id, countersVisibility);
      if (!key) {
        return false;
      }

      return Boolean(findBadgeCounter(counters, countersVisibility, key));
    },
  );
}

const mapStateToProps = makeMapStateToProps<StartGuideContainerProps>()(() => {
  const getTeamNamespace = makeGetTeamNamespace();
  const getCountersVisibility = makeGetCountersVisibility(getTeamNamespace);

  return {
    user: getUser,
    userNamespace: getUserNamespace,
    lastUpdatedTeam: getLastUpdatedTeam,
    hasConversationsInTeamInbox: makeGetHasConversationsInTeamInbox(getCountersVisibility),
    shouldShowAttendLiveTrainingTask: getIsCustomer,
    shouldAddTeamInbox: getShouldAddTeamInbox,
    shouldAddTeammate: getShouldAddTeammate,
    shouldAddIndividualInbox: getShouldAddIndividualInbox,
    emailComposeChannel: makeGetComposeChannelExcludingDemo({
      getSpecifiedMessageType: () => MessageTypesEnum.EMAIL,
    }),
    completedStartGuideTasks: getCompletedStartGuideTasks,
    appPath: makeGetAppPath(makeGetAppPanelIdFromPanel()),
    hasGlobalAdminStartGuide: getHasGlobalAdminStartGuide,
    company: getCompany,
    userMetadata: getUserMetadata,
    isUserFirstGlobalAdmin: getIsUserFirstGlobalAdmin,
    hasExploredDemoInbox: getHasExploredDemoInbox,
    firstTeamUserIsAdminOf: getFirstTeamUserIsAdminOf,
    isUserGlobalAdmin: getIsUserGlobalAdmin,
    savedHelpCenterCache: getHelpCenterSearchCache,
    shouldExploreSettings: getShouldExploreSettings,
    hasActionCtasBundle: getHasActionCtasBundle,
    hasNewTeammateInvite: getHasNewTeammateInvite,
  };
});

export const StartGuideContainer = connect(
  mapStateToProps,
  {
    navigateToLinkAction,
    composeTeamInboxAccessRequest,
    composeMessageAction,
    updateStartGuideTasksAction,
    openHelpChat: openHelpChatAction,
    navigateToAddTeammate: navigateToAddTeammateAction,
    updateHelpCenterSearchCache: updateHelpCenterSearchCacheAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const {panelId} = ownProps;
    const {
      user,
      isUserFirstGlobalAdmin,
      isUserGlobalAdmin,
      userNamespace,
      lastUpdatedTeam,
      hasConversationsInTeamInbox,
      shouldAddTeamInbox,
      shouldAddTeammate,
      shouldAddIndividualInbox,
      emailComposeChannel,
      completedStartGuideTasks,
      appPath,
      hasGlobalAdminStartGuide,
      company,
      shouldShowAttendLiveTrainingTask,
      hasExploredDemoInbox,
      shouldExploreSettings,
      hasActionCtasBundle,
    } = stateProps;

    // Link for contact us
    const onOpenHelpChatClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_TASK_CLICK, {
        category: 'help',
        user_track: hasGlobalAdminStartGuide ? 'admin' : 'non_admin',
        item: 'talk_to_us',
      });
      dispatchProps.openHelpChat(panelId);
    };

    // Link for our blog
    const onOpenBlogClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_TASK_CLICK, {
        category: 'help',
        user_track: hasGlobalAdminStartGuide ? 'admin' : 'non_admin',
        item: 'our_blog',
      });
      dispatchProps.navigateToLinkAction(blogUrl);
    };

    // Default to adding inboxes and answers to the last updated team, which is likely the only one anyway.
    const targetNamespace = lastUpdatedTeam && lastUpdatedTeam.namespace;

    // Get location to determine if we are viewing a conversation. Assume we
    // are in a conversation if last part of the path is greater than 0.
    // TODO: Use the viewer panel state to determine this.
    const lastPathElement = last(appPath.split('/'));
    const isConversation = Boolean(lastPathElement && parseInt(lastPathElement, 10) > 0);

    const companyName = company.name && company.name !== '[Unknown Company Name]' ? company.name : null;

    // Create account task. This is a "dummy" task to give the user a sense of progress/momentum.
    let createAccountTask: StartGuideTaskDefinition | undefined;
    if (!isUserFirstGlobalAdmin) {
      createAccountTask = {
        title: <Trans id="startguide-task-title-createaccount">Complete Front tour</Trans>,
        description: <Trans id="startguide-task-description-createaccount">{'< 3 min'}</Trans>,
        interactionId: StartGuideTaskInteractionsEnum.TOUR_INTRO,
        iconName: 'sparklesSmall',
        isIncomplete: false,
        onClick: noop,
      };
    }

    let exploreDemoInboxTask: StartGuideTaskDefinition | undefined;
    if (isUserFirstGlobalAdmin && hasActionCtasBundle) {
      exploreDemoInboxTask = {
        title: <Trans id="startguide-task-title-exploredemoinbox">Explore sample inbox</Trans>,
        description: <Trans id="startguide-task-description-exploredemoinbox">{'< 1 min'}</Trans>,
        isIncomplete: !hasExploredDemoInbox,
        interactionId: StartGuideTaskInteractionsEnum.EXPLORE_DEMO_INBOX,
        iconName: 'sparklesSmall',
        renderTooltip: () => (
          <Trans id="startguide-task-tooltip-exploredemoinbox">
            Hover over the pink tooltips in the sample inbox to learn more about Front.
          </Trans>
        ),
        onClick: noop,
      };
    }

    /**
     * Add team inbox task.
     *
     * Trigger: User is a global admin in trial.
     * Action: Send to team inbox page in settings.
     * Completion: Access to a team inbox.
     */
    let addTeamInboxTask: StartGuideTaskDefinition | undefined;
    if (targetNamespace) {
      addTeamInboxTask = {
        title: <Trans id="startguide-task-title-teaminbox">Add a team inbox</Trans>,
        description: <Trans id="startguide-task-description-teaminbox">{'< 3 min'}</Trans>,
        interactionId: StartGuideTaskInteractionsEnum.ADD_TEAM_INBOX,
        iconName: 'inboxSmall',
        isIncomplete: shouldAddTeamInbox,
        renderTooltip: () => (
          <Trans id="startguide-task-tooltip-teaminbox">Work together on messages as a team</Trans>
        ),
        onClick: () => dispatchProps.navigateToLinkAction(buildAddInboxSettingsPath(targetNamespace)),
      };
    }

    /**
     * Add teammate task.
     *
     * Trigger: User is a global admin in trial.
     * Action: Send to teammate invite page in settings.
     * Completion: Teammate added to company.
     */
    const addTeammateTask: StartGuideTaskDefinition = {
      title: <Trans id="startguide-task-title-addteammate">Invite teammates</Trans>,
      description: <Trans id="startguide-task-description-addteammate">{'< 1 min'}</Trans>,
      interactionId: StartGuideTaskInteractionsEnum.INVITE_TEAM,
      iconName: 'teammatesSmall',
      isIncomplete: shouldAddTeammate,
      renderTooltip: () => (
        <Trans id="startguide-task-tooltip-addteammate">Collaborate in Front with a teammate</Trans>
      ),
      onClick: () =>
        dispatchProps.navigateToAddTeammate(
          panelId,
          buildAddTeammateSettingsPath(stateProps.hasNewTeammateInvite),
          InviteTeammatePopoverAccessPointsEnum.START_GUIDE,
        ),
    };

    /**
     * Download app task.
     *
     * Trigger: All startGuide users get this task.
     * Action: Send user to download page on website and complete the download
     * app startGuide task.
     * Completion: Click this task or use the desktop app.
     */
    const downloadAppTask: StartGuideTaskDefinition = {
      title: <Trans id="startguide-task-title-download">Download desktop app</Trans>,
      description: <Trans id="startguide-task-description-download">{'< 30 sec'}</Trans>,
      interactionId: StartGuideTaskInteractionsEnum.DOWNLOAD_APP,
      iconName: 'downloadSmall',
      isIncomplete: !isElectron() && !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.DOWNLOAD_APP),
      renderTooltip: () => (
        <Trans id="startguide-task-tooltip-download">Work in Front from your desktop</Trans>
      ),
      onClick: () => {
        dispatchProps.updateStartGuideTasksAction(StartGuideTaskTypesEnum.DOWNLOAD_APP);
        openInBrowser('https://frontapp.com/download');
      },
    };

    /**
     * Add individual inbox task.
     *
     * Trigger: All startGuide users get this task.
     * Action: Send to user inbox page in settings.
     * Completion: Add an individual inbox.
     */
    const addIndividualInboxTask: StartGuideTaskDefinition = {
      title: <Trans id="startguide-task-title-individualinbox">Add your work email</Trans>,
      description: <Trans id="startguide-task-description-individualinbox">{'< 1 min'}</Trans>,
      interactionId: StartGuideTaskInteractionsEnum.ADD_WORK_EMAIL,
      iconName: 'messageSmall',
      isIncomplete: shouldAddIndividualInbox,
      renderTooltip: () => (
        <Trans id="startguide-task-tooltip-individualinbox">Consolidate all your work in one place</Trans>
      ),
      onClick: () => noop,
    };

    /**
     * Attend live training session task.
     *
     * Trigger: Non-admin teammate.
     * Action: Open link to webinar registration.
     * Completion: Click the link.
     */

    let attendLiveTrainingTask: StartGuideTaskDefinition | undefined;
    if (shouldShowAttendLiveTrainingTask) {
      attendLiveTrainingTask = {
        title: <Trans id="startguide-task-title-attendlivetraining">Attend a live training</Trans>,
        description: <Trans id="startguide-task-description-attendlivetraining">{'< 5 min'}</Trans>,
        interactionId: StartGuideTaskInteractionsEnum.ATTEND_LIVE_TRAINING,
        iconName: 'calendarInvite',
        isIncomplete: !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.ATTEND_LIVE_TRAINING),
        onClick: () => {
          openInBrowser(
            'https://frontapp.com/resources/webinar-teach-me-how-to-front-or-live-training?utm_source=frontapp&utm_campaign=SCOD22_MCOD7_CRCO15&utm_medium=in_app&utm_content=p4_start_guide',
          );
          dispatchProps.updateStartGuideTasksAction(StartGuideTaskTypesEnum.ATTEND_LIVE_TRAINING);
        },
      };
    }

    /**
     * Request access to team inbox task.
     *
     * Trigger: User is an invited teammate.
     * Action: Open a draft discussion to request access to a team inbox.
     * Completion: Teammate has access to a team inbox.
     */
    const requestTeamInboxTask: StartGuideTaskDefinition = {
      title: <Trans id="startguide-task-title-requestteaminbox">Access your team inbox</Trans>,
      description: <Trans id="startguide-task-description-requestteaminbox">{'< 1 min'}</Trans>,
      interactionId: StartGuideTaskInteractionsEnum.ACCESS_TEAM_INBOX,
      iconName: 'inboxSmall',
      isIncomplete: shouldAddTeamInbox,
      onClick: dispatchProps.composeTeamInboxAccessRequest,
    };

    /**
     * Send email task.
     *
     * Trigger: User is an invited teammate.
     * Action: Open an email composer.
     * Completion: Teammate has sent an email.
     */
    const sendEmailTask: StartGuideTaskDefinition = {
      title: <Trans id="startguide-task-title-sendemail">Send your first email</Trans>,
      description: <Trans id="startguide-task-description-sendemail">{'< 1 min'}</Trans>,
      interactionId: StartGuideTaskInteractionsEnum.SEND_FIRST_EMAIL,
      iconName: 'sendSmall',
      isIncomplete: !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.SEND_EMAIL),
      isDisabled: !emailComposeChannel,
      onClick: () => {
        if (!emailComposeChannel) {
          return;
        }

        dispatchProps.composeMessageAction({channelId: emailComposeChannel.id});
      },
    };

    /**
     * Mention teammate in comment task.
     *
     * Trigger: User is an invited teammate.
     * Action: Show infobubble around comment bar when a conversation is
     * visible in P3.
     * Completion: Teammate has mentioned a teammate.
     */
    const mentionTeammateTask: StartGuideTaskDefinition = {
      title: <Trans id="startguide-task-title-mentionteammate">@mention a teammate</Trans>,
      description: <Trans id="startguide-task-description-mentionteammate">{'< 1 min'}</Trans>,
      interactionId: StartGuideTaskInteractionsEnum.MENTION_TEAMMATE,
      iconName: 'mentionSmall',
      isIncomplete: !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.MENTION_TEAMMATE),
      isDisabled: !isConversation,
      renderTooltip: () =>
        isConversation ? null : (
          <Trans id="startguide-task-tooltip-mentionteammate">Please open a message first</Trans>
        ),
      infoBubble: OnboardingTipTypesEnum.START_GUIDE_COMMENT_COMPOSER,
      onClick: noop,
    };

    /**
     * Explore company settings task.
     *
     * Action: Navigate to team rules or settings homepage (if available).
     */
    let exploreSettingsTask: StartGuideTaskDefinition | undefined;
    if (targetNamespace) {
      exploreSettingsTask = {
        title: <Trans id="startguide-task-title-exploresettings">Explore settings</Trans>,
        description: <Trans id="startguide-task-description-exploresettings">{'< 3 min'}</Trans>,
        interactionId: StartGuideTaskInteractionsEnum.EXPLORE_SETTINGS,
        iconName: 'gearSmall',
        isIncomplete: shouldExploreSettings,
        renderTooltip: () => (
          <Trans id="startguide-task-tooltip-exploresettings">
            Setup your workflow with rules, tags, and responses in settings
          </Trans>
        ),
        onClick: () => {
          dispatchProps.navigateToLinkAction('/settings/home');

          dispatchProps.updateStartGuideTasksAction(StartGuideTaskTypesEnum.ADD_TEAM_RULES);
        },
      };
    }

    const nonGlobalAdminTasks = compact([
      createAccountTask,
      requestTeamInboxTask,
      mentionTeammateTask,
      addIndividualInboxTask,
      sendEmailTask,
      downloadAppTask,
      attendLiveTrainingTask,
    ]);

    const newCompanyCutoffTime = DateTime.local().minus({months: 6}).toMillis();
    const isNewCompany = company.createdAt >= newCompanyCutoffTime;
    // Companies 6 months or younger should see the 1-on-1 training task plus
    // other actions.
    // Otherwise, they should only see the live training task.
    const globalAdminTasks = isNewCompany
      ? compact([
          createAccountTask,
          exploreDemoInboxTask,
          addTeammateTask,
          addTeamInboxTask,
          exploreSettingsTask,
          addIndividualInboxTask,
          downloadAppTask,
        ])
      : compact([attendLiveTrainingTask]);

    // Success state for tasks
    const hasCompletedUserTasks = nonGlobalAdminTasks.every((task) => !task.isIncomplete);
    const hasCompletedGlobalAdminTasks = globalAdminTasks.every((task) => !task.isIncomplete);
    const hasCompletedAllTasks = hasGlobalAdminStartGuide
      ? hasCompletedGlobalAdminTasks
      : hasCompletedUserTasks;

    // Only show tasks if user account is under two weeks or doesn't have the feature flag
    const newUserTaskCuttoffTime = DateTime.local().minus({weeks: 4}).toMillis();
    const isNewUser = user.createdAt >= newUserTaskCuttoffTime;
    const shouldShowStartGuideTasks = isNewUser && !hasCompletedUserTasks;

    const shouldShowSuccessScreen =
      isNewUser &&
      hasCompletedAllTasks &&
      !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.HELP_CENTER_SUCCESS_DISMISS);

    const onDismissSuccessScreen = () =>
      dispatchProps.updateStartGuideTasksAction(StartGuideTaskTypesEnum.HELP_CENTER_SUCCESS_DISMISS);

    const navigateToTeamInbox = () => {
      if (!lastUpdatedTeam) {
        return;
      }

      const teamInboxPath = buildTeamPath(
        lastUpdatedTeam.id,
        TeamsFoldersEnum.INBOX,
        InboxesFiltersEnum.OPEN,
      );
      dispatchProps.navigateToLinkAction(teamInboxPath);
    };

    const openEmailComposer = () => {
      if (emailComposeChannel) {
        dispatchProps.composeMessageAction({channelId: emailComposeChannel.id});
      }
    };
    const navigate = (destination: string) => () => dispatchProps.navigateToLinkAction(destination);

    const onSaveSearchQuery = (query?: string, result?: SearchHelpCenterResultModel) => {
      dispatchProps.updateHelpCenterSearchCache(query, result);
    };

    return {
      givenName: user.givenName,
      tasks: hasGlobalAdminStartGuide ? globalAdminTasks : nonGlobalAdminTasks,
      isUserFirstGlobalAdmin,
      isGlobalAdmin: isUserGlobalAdmin,
      company,
      companyName,
      userNamespace,
      hasEmailComposeChannel: Boolean(emailComposeChannel),
      shouldShowStartGuideTasks,
      shouldShowSuccessScreen,
      shouldAddTeamInbox,
      shouldShowOpenTeamInbox: hasConversationsInTeamInbox,
      composeTeamInboxAccessRequest: dispatchProps.composeTeamInboxAccessRequest,
      onDismissSuccessScreen,
      openEmailComposer,
      navigateToTeamInbox,
      onOpenHelpChatClick,
      onOpenBlogClick,
      navigate,
      initialSearchCache: stateProps.savedHelpCenterCache,
      onSaveSearchQuery,
    };
  },
)(StartGuide);
